/** Dev21 — Venue. Location card beside a live, embedded Google Map. */

import React from 'react';
import { MapPin, Navigation } from 'lucide-react';
import { Section, SectionHeading, Reveal } from '../components/primitives';
import type { Dev21Content } from '../content';

export function VenueSection({
  content,
}: {
  content: Dev21Content['venue'];
}) {
  const query = encodeURIComponent(content.mapQuery);
  // `output=embed` renders an interactive map without an API key.
  const embedSrc = `https://www.google.com/maps?q=${query}&output=embed`;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${query}`;

  return (
    <Section id="venue" tone="cream">
      <SectionHeading label={content.label} title={content.title} />

      <div className="mt-12 grid gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-stretch">
        {/* Location card */}
        <Reveal>
          <div className="flex h-full flex-col justify-between gap-6 rounded-3xl border border-[var(--d21-border)] bg-[var(--d21-surface)] p-7 shadow-[0_28px_64px_-44px_rgba(33,26,19,0.55)] sm:p-9">
            <div>
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--d21-accent)]/12 text-[var(--d21-accent)]">
                <MapPin size={22} />
              </span>
              <h3 className="mt-5 font-serif text-2xl font-semibold text-[var(--d21-ink)]">
                {content.name}
              </h3>
              <p className="mt-2 text-base leading-relaxed text-[var(--d21-muted)]">
                {content.address}
              </p>
            </div>
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-fit items-center gap-2 rounded-full bg-[var(--d21-accent)] px-6 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-white transition hover:-translate-y-0.5 hover:bg-[var(--d21-ink)]"
            >
              <Navigation size={16} />
              Get Directions
            </a>
          </div>
        </Reveal>

        {/* Embedded interactive map */}
        <Reveal delay={0.12}>
          <div className="h-full min-h-[320px] overflow-hidden rounded-3xl border border-[var(--d21-border)] shadow-[0_28px_64px_-44px_rgba(33,26,19,0.55)]">
            <iframe
              title={`Map to ${content.name}`}
              src={embedSrc}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="h-full min-h-[320px] w-full border-0"
              allowFullScreen
            />
          </div>
        </Reveal>
      </div>
    </Section>
  );
}
